import { supabase } from '~/common/utils/supabase.util.ts';

import type { AuthUser, SigninRequest, SignupRequest } from '~/features/auth/types/auth.type';

/**
 * 인증 어댑터 — Supabase Auth.
 *
 * @description 인증과 데이터를 한 시스템에 둔다. 종전처럼 인증(enter-launcher-api)과 데이터(Supabase)가
 *  갈려 있으면 두 신원을 이어붙이는 장치가 필요하고, 그 장치가 깨지는 순간 "로그인은 됐는데 내 데이터가
 *  없는" 상태가 된다(실제로 발생). 여기서는 `auth.users.id` 하나가 곧 데이터 소유자다.
 *
 *  에러는 **문구로 번역해서** 던진다 — Supabase 원문("Invalid login credentials")을 그대로 노출하면
 *  사용자가 무엇을 고쳐야 할지 알 수 없다.
 */

/** Supabase 세션 사용자 → 화면용 타입. 이메일이 없는 계정은 우리 가입 경로에 존재하지 않는다. */
function toAuthUser(user: { id: string; email?: string }): AuthUser {
  return { id: user.id, email: user.email ?? '' };
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  const user = data.session?.user;
  return user ? toAuthUser(user) : null;
}

export async function signInApi({ email, password }: SigninRequest): Promise<AuthUser> {
  if (!supabase) throw new Error('인증 설정이 없습니다.');

  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });

  if (error || !data.user) {
    throw new Error('이메일 또는 비밀번호가 일치하지 않습니다.');
  }
  return toAuthUser(data.user);
}

export async function signUpApi({
  email,
  password,
  role,
  tutorName,
}: SignupRequest): Promise<AuthUser> {
  if (!supabase) throw new Error('인증 설정이 없습니다.');

  const { data, error } = await supabase.auth.signUp({
    email: email.trim().toLowerCase(),
    password,
    // 메타데이터에 남겨 두면 프로필 생성이 실패해도 "무슨 역할로 가입했는지"를 잃지 않는다.
    options: { data: { role } },
  });

  if (error) {
    // Supabase 는 이미 가입된 이메일에도 성공을 돌려주는 설정이 있어 메시지로 구분한다.
    if (/already/i.test(error.message)) throw new Error('이미 등록된 이메일입니다.');
    if (/password/i.test(error.message)) throw new Error('비밀번호는 6자 이상이어야 합니다.');
    throw new Error('회원가입에 실패했습니다. 잠시 후 다시 시도해주세요.');
  }
  if (!data.user) throw new Error('회원가입에 실패했습니다.');

  /*
    지도사는 프로필을 여기서 만든다.
    보호자는 온보딩(반려견 등록)에서 프로필이 생기지만 지도사에게는 그 단계가 없다 — 프로필이 없으면
    신청을 받을 수도, 콘솔에 아무것도 띄울 수도 없으므로 가입과 동시에 만들어야 한다.
    slug 는 비워 둔다. 공개 주소는 slug 가 없으면 프로필 id 로 대체된다(`tutor.api` 의 toTutor).
  */
  if (role === 'tutor') {
    const { error: profileError } = await supabase.from('profiles').insert({
      /* eslint-disable-next-line @typescript-eslint/naming-convention -- DB 컬럼명 */
      auth_user_id: data.user.id,
      role: 'tutor',
      nickname: (tutorName ?? '').trim() || email.trim().split('@')[0],
    });
    // 계정은 이미 만들어졌다 — 프로필만 실패했음을 정확히 알려야 사용자가 다시 가입을 시도하지 않는다.
    if (profileError) {
      throw new Error(
        '계정은 만들어졌지만 지도사 프로필 생성에 실패했습니다. 로그인 후 다시 시도해주세요.',
      );
    }
  }

  return toAuthUser(data.user);
}

export async function signOutApi(): Promise<void> {
  if (!supabase) return;
  await supabase.auth.signOut();
}

/**
 * 비밀번호 재설정 메일 발송.
 *
 * @description 원본 시안(A17)은 **6자리 인증번호** 방식이지만, Supabase 무료 플랜은 기본 발송자를
 *  쓸 때 이메일 템플릿 수정을 막는다(`Email template modification is not available for free tier`).
 *  템플릿을 못 바꾸면 본문에 인증번호를 실을 수 없어 기본 제공되는 **링크 방식**으로 간다.
 *  커스텀 SMTP를 붙이거나 유료 전환하면 그때 OTP 방식으로 바꿀 수 있다.
 *
 *  ⚠️ 무료 플랜의 발송 한도는 **시간당 2통**이다. 연속 시도 시 조용히 실패하는 대신
 *  호출부가 안내할 수 있도록 에러를 문구로 번역해 던진다.
 */
export async function requestPasswordResetApi(email: string): Promise<void> {
  if (!supabase) throw new Error('인증 설정이 없습니다.');

  const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
    // 메일의 링크가 도착할 화면. Supabase 허용 목록(uri_allow_list)에 등록돼 있어야 한다.
    redirectTo: `${window.location.origin}/auth/reset-password`,
  });

  if (error) {
    if (/rate|limit|too many/i.test(error.message)) {
      throw new Error('잠시 후 다시 시도해주세요. (메일 발송이 잠시 제한되었습니다)');
    }
    throw new Error('메일 발송에 실패했습니다. 이메일 주소를 확인해주세요.');
  }
}

/**
 * 새 비밀번호로 변경.
 * @description 메일 링크로 진입하면 supabase-js 가 URL 의 복구 토큰을 읽어 **임시 세션**을 만든다.
 *  그 세션이 있어야 이 호출이 통과하므로, 링크 없이 이 화면에 직접 오면 실패한다(의도된 동작).
 */
export async function updatePasswordApi(password: string): Promise<void> {
  if (!supabase) throw new Error('인증 설정이 없습니다.');

  const { error } = await supabase.auth.updateUser({ password });
  if (error) {
    if (/session|token|expired/i.test(error.message)) {
      throw new Error('링크가 만료되었습니다. 비밀번호 찾기를 다시 시도해주세요.');
    }
    if (/password/i.test(error.message)) throw new Error('비밀번호는 6자 이상이어야 합니다.');
    throw new Error('비밀번호 변경에 실패했습니다.');
  }
}
